import { create } from 'zustand';

const BALANCE_HIDDEN_KEY = 'incpt.balanceHidden';
const COIN_BALANCE_HIDDEN_KEY = 'incpt.coinBalanceHidden';

function readBalanceHidden(): boolean {
  try {
    return localStorage.getItem(BALANCE_HIDDEN_KEY) === '1';
  } catch {
    return false;
  }
}

function readCoinBalanceHidden(): Record<string, boolean> {
  try {
    const raw = localStorage.getItem(COIN_BALANCE_HIDDEN_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as unknown;
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) return {};
    const out: Record<string, boolean> = {};
    for (const [k, v] of Object.entries(parsed)) {
      if (v === true) out[k] = true;
    }
    return out;
  } catch {
    return {};
  }
}

function writeCoinBalanceHidden(map: Record<string, boolean>) {
  try {
    localStorage.setItem(COIN_BALANCE_HIDDEN_KEY, JSON.stringify(map));
  } catch {
    /* private mode / quota */
  }
}

type UiState = {
  /** Number of open bottom sheets. The bottom nav hides while > 0. */
  sheetCount: number;
  openSheet: () => void;
  closeSheet: () => void;
  /** Privacy: hide balances on home / wallet surfaces. Persisted locally. */
  balanceHidden: boolean;
  setBalanceHidden: (hidden: boolean) => void;
  toggleBalanceHidden: () => void;
  /** Per-coin privacy on coin detail screens. Key = coin id (e.g. INCPT, USDT-tron). */
  coinBalanceHidden: Record<string, boolean>;
  isCoinBalanceHidden: (coinId: string) => boolean;
  toggleCoinBalanceHidden: (coinId: string) => void;
};

export const useUiStore = create<UiState>((set, get) => ({
  sheetCount: 0,
  openSheet: () => set((s) => ({ sheetCount: s.sheetCount + 1 })),
  closeSheet: () => set((s) => ({ sheetCount: Math.max(0, s.sheetCount - 1) })),
  balanceHidden: readBalanceHidden(),
  setBalanceHidden: (hidden) => {
    try {
      localStorage.setItem(BALANCE_HIDDEN_KEY, hidden ? '1' : '0');
    } catch {
      /* private mode / quota */
    }
    set({ balanceHidden: hidden });
  },
  toggleBalanceHidden: () => get().setBalanceHidden(!get().balanceHidden),
  coinBalanceHidden: readCoinBalanceHidden(),
  isCoinBalanceHidden: (coinId) => !!get().coinBalanceHidden[coinId],
  toggleCoinBalanceHidden: (coinId) => {
    const next = { ...get().coinBalanceHidden };
    if (next[coinId]) delete next[coinId];
    else next[coinId] = true;
    writeCoinBalanceHidden(next);
    set({ coinBalanceHidden: next });
  },
}));

/** Placeholder shown instead of a dollar amount when balances are hidden. */
export const HIDDEN_BALANCE = '$••••••';

/** Stable id for the off-chain INCPT coin detail screen. */
export const INCPT_COIN_ID = 'INCPT';

/** Privacy key for staking hero + staking slice of the home total. */
export const STAKING_BALANCE_ID = 'STAKING';
